import * as React from "react";
import ResetConfirmDialog from "./additional/reset-confirm-dialog";
import ConversationArea, { Message } from "./conversation/area";
import Composer from "./conversation/composer";
import InterviewAvatar from "./interview-avatar";
import InterviewHeader from "./interview-header";
import { useState } from "react";
import { IntroData } from "./additional/intro-block";

interface IInterviewChatProps {
  introData?: IntroData;

  messages?: Message[];
  usedMessages?: number;
  isProcessing?: boolean;
  lastMessageTime?: number;

  totalMessages?: number;
  warningThreshold?: number;
  usedTokens?: number;
  totalTokens?: number;

  isConnectionError?: boolean;
  isSendDisabled?: boolean;

  sendMessage: (text: string) => void;
  handleReset: () => void;
  handleRetryConnection?: () => void;
}

const InterviewChat: React.FC<IInterviewChatProps> = ({
  introData = { title: "", description: "", questionsTitle: "", questions: [] },
  sendMessage,
  messages = [],
  usedMessages = 0,
  isProcessing = false,
  lastMessageTime = undefined,
  totalMessages = 20,
  warningThreshold = 16,
  usedTokens = 0,
  totalTokens = 10000,
  isConnectionError = false,
  isSendDisabled = false,
  handleRetryConnection,
  handleReset,
}) => {
  const [showResetDialog, setShowResetDialog] = useState(false);

  const getHeaderVariant = (): "normal" | "warning" | "hard-stop" => {
    if (usedMessages >= totalMessages) return "hard-stop";
    if (usedMessages >= warningThreshold) return "warning";
    return "normal";
  };

  const getWarningText: () => string | undefined = () => {
    if (usedMessages >= totalMessages) {
      return "Сейчас я не смогу продолжить беседу: лимит этого режима уже исчерпан.";
    }
    if (usedMessages >= warningThreshold) {
      return "Остаток лимита почти исчерпан. Следующего полного ответа может уже не хватить.";
    }
    return undefined;
  };

  // вынести на уровень выше
  const canSendMessage = usedMessages < totalMessages && !isProcessing;

  return (
    <>
      {/* Main content */}
      <div className="flex-1 flex flex-col w-full max-w-[640px] mx-[20px] bg-white rounded-[10px] border border-[#ddd] overflow-hidden md:mb-[20px]">
        <InterviewHeader
          usedMessages={usedMessages}
          totalMessages={totalMessages}
          usedTokens={usedTokens}
          totalTokens={totalTokens}
          onReset={() => setShowResetDialog(true)}
          variant={getHeaderVariant()}
          avatar={
            <InterviewAvatar
              compact
              isProcessing={isProcessing}
              lastMessageTime={lastMessageTime}
              hasMessages={messages.length > 0}
            />
          }
        />

        <ConversationArea
          messages={messages}
          showIntro={messages.length === 0}
          warningText={getWarningText()}
          introData={introData}
          warningVariant={
            usedMessages >= totalMessages ? "hard-stop" : "warning"
          }
          connectionError={isConnectionError}
          onQuestionClick={sendMessage}
          onRetryConnection={handleRetryConnection}
        />
        <Composer onSend={sendMessage} disabled={!canSendMessage || isSendDisabled} />
      </div>

      {/* Reset confirmation dialog */}
      <ResetConfirmDialog
        isOpen={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onConfirm={handleReset}
        showLowLimitWarning={usedMessages >= warningThreshold}
      />
    </>
  );
};

export default InterviewChat;
